import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCreditCard, FiDollarSign, FiDownload, FiCalendar, FiCheck,
  FiX, FiPlus, FiEdit2, FiTrash2, FiAlertCircle, FiStar,
  FiZap, FiShield, FiTrendingUp, FiUsers, FiMessageSquare,
  FiDatabase, FiClock, FiArrowRight, FiGift, FiAward
} from 'react-icons/fi';
import { BiBot, BiBrain } from 'react-icons/bi';
import { BsRobot } from 'react-icons/bs';
import toast from 'react-hot-toast';
import api from '../utils/api';

interface Plan {
  name: string;
  price: number;
  period: 'month' | 'year';
  features: string[];
  color: string;
  popular?: boolean;
  current?: boolean;
}

interface Invoice {
  id: string;
  date: Date;
  amount: number;
  status: 'paid' | 'pending' | 'failed';
  description: string;
  downloadUrl?: string;
}

interface PaymentMethod {
  id: string;
  type: 'card' | 'paypal';
  last4?: string;
  brand?: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
}

const Billing: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [currentUsage, setCurrentUsage] = useState({
    conversations: 0,
    messages: 0,
    storage: 0,
    apiCalls: 0
  });
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  const plans: Plan[] = [
    {
      name: 'Starter',
      price: billingPeriod === 'monthly' ? 29 : 290,
      period: billingPeriod === 'monthly' ? 'month' : 'year',
      color: 'from-blue-500 to-cyan-500',
      features: [
        '1,000 conversations/month',
        '10,000 messages/month',
        '5 AI assistants',
        '1GB storage',
        'Email support',
        'Basic analytics'
      ]
    },
    {
      name: 'Professional',
      price: billingPeriod === 'monthly' ? 79 : 790,
      period: billingPeriod === 'monthly' ? 'month' : 'year',
      color: 'from-purple-500 to-pink-500',
      popular: true,
      current: true,
      features: [
        '10,000 conversations/month',
        '100,000 messages/month',
        '25 AI assistants',
        '10GB storage',
        'Priority support',
        'Advanced analytics',
        'Custom integrations',
        'API access'
      ]
    },
    {
      name: 'Enterprise',
      price: billingPeriod === 'monthly' ? 199 : 1990,
      period: billingPeriod === 'monthly' ? 'month' : 'year',
      color: 'from-green-500 to-emerald-500',
      features: [
        'Unlimited conversations',
        'Unlimited messages',
        'Unlimited AI assistants',
        '100GB storage',
        'Dedicated support',
        'Custom analytics',
        'White-label solution',
        'SLA guarantee',
        'On-premise deployment'
      ]
    }
  ];

  useEffect(() => {
    const loadBilling = async () => {
      try {
        const usageRsp = await api.get('/billing/usage');
        const summary = usageRsp.data?.summary || {};
        setCurrentUsage({
          conversations: Number(summary.conversation?.count || 0),
          messages: Number(summary.message?.count || 0),
          storage: Number(summary.storage_gb?.count || 0),
          apiCalls: Number(summary.api_call?.count || 0)
        });
      } catch {}
      try {
        const invRsp = await api.get('/billing/invoices');
        const list: Invoice[] = (invRsp.data?.invoices || []).map((inv: any) => ({
          id: inv.id || inv.number || invRsp.data?.id || String(Date.now()),
          date: new Date(inv.created ? inv.created * 1000 : Date.now()),
          amount: inv.amount_due != null ? inv.amount_due / 100 : (inv.amount || 0),
          status: ((inv.status || 'paid').toString().toLowerCase() as any) === 'paid' ? 'paid' : (((inv.status || '').toString().toLowerCase() === 'open') ? 'pending' : 'failed'),
          description: inv.description || inv.customer_name || 'Invoice',
          downloadUrl: inv.hosted_invoice_url || inv.invoice_pdf || undefined
        }));
        setInvoices(list);
      } catch {}
    };
    loadBilling();
  }, []);

  const paymentMethods: PaymentMethod[] = [
    {
      id: '1',
      type: 'card',
      brand: 'Visa',
      last4: '4242',
      expiryMonth: 12,
      expiryYear: 2025,
      isDefault: true
    },
    {
      id: '2',
      type: 'card',
      brand: 'Mastercard',
      last4: '8888',
      expiryMonth: 8,
      expiryYear: 2024,
      isDefault: false
    }
  ];

  const usageLimits = {
    conversations: { current: currentUsage.conversations, limit: 10000, unit: 'conversations' },
    messages: { current: currentUsage.messages, limit: 100000, unit: 'messages' },
    storage: { current: currentUsage.storage, limit: 10, unit: 'GB' },
    apiCalls: { current: currentUsage.apiCalls, limit: 50000, unit: 'API calls' }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'text-green-400';
      case 'pending': return 'text-yellow-400';
      case 'failed': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <FiCheck size={16} />;
      case 'pending': return <FiClock size={16} />;
      case 'failed': return <FiX size={16} />;
      default: return <FiAlertCircle size={16} />;
    }
  };

  const planKeyFor = (name: string): 'basic' | 'pro' | 'enterprise' => {
    const n = name.toLowerCase();
    if (n.startsWith('starter') || n.startsWith('basic')) return 'basic';
    if (n.startsWith('pro') || n.startsWith('professional')) return 'pro';
    return 'enterprise';
  };

  const handleUpgrade = async (planName: string) => {
    try {
      const plan = planKeyFor(planName);
      const { data } = await api.post('/billing/checkout-session', { plan, billingPeriod });
      const url = data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      toast.error('Checkout session not available');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Upgrade failed');
    }
  };

  const handleManageBilling = async () => {
    try {
      const { data } = await api.post('/billing/customer-portal', {});
      if (data?.url) window.open(data.url, '_blank');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Customer portal unavailable');
    }
  };

  const AddPaymentModal = () => (
    <AnimatePresence>
      {showAddPayment && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowAddPayment(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative z-10 w-full max-w-md rounded-2xl p-6"
            style={{
              background: 'rgba(30, 41, 59, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <h2 className="text-xl font-bold text-white mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Add Payment Method
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Card Number</label>
                <input
                  type="text"
                  placeholder="1234 5678 9012 3456"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Expiry</label>
                  <input
                    type="text"
                    placeholder="MM/YY"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">CVC</label>
                  <input
                    type="text"
                    placeholder="123"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Cardholder Name</label>
                <input
                  type="text"
                  placeholder="John Doe"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={async () => {
                    setShowAddPayment(false);
                    toast.success('Payment method added successfully!');
                    try {
                      await api.get('/billing/health');
                    } catch {}
                  }}
                  className="flex-1 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium"
                >
                  Add Card
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowAddPayment(false)}
                  className="px-4 py-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700"
                >
                  Cancel
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.1, 0.3, 0.1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 18 + i * 2,
              repeat: Infinity,
              delay: i * 0.5,
            }}
          >
            <FiDollarSign size={30 + i * 5} className="text-green-500/20" />
          </motion.div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
            Billing & Usage
          </h1>
          <p className="text-gray-400 mt-2">Manage your subscription and monitor usage</p>
        </motion.div>

        {/* Current Usage Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          {Object.entries(usageLimits).map(([key, data], index) => {
            const percentage = (data.current / data.limit) * 100;
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl"
                style={{
                  background: 'rgba(30, 41, 59, 0.8)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold capitalize">
                    {key.replace(/([A-Z])/g, ' $1')}
                  </h3>
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${
                    percentage > 80 ? 'from-red-500 to-orange-500' :
                    percentage > 60 ? 'from-yellow-500 to-orange-500' :
                    'from-green-500 to-emerald-500'
                  }`}>
                    {key === 'conversations' ? <FiMessageSquare size={16} /> :
                     key === 'messages' ? <BiBot size={16} /> :
                     key === 'storage' ? <FiDatabase size={16} /> :
                     <FiZap size={16} />}
                  </div>
                </div>
                
                <div className="mb-3">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-400">{data.current.toLocaleString()}</span>
                    <span className="text-gray-400">{data.limit.toLocaleString()} {data.unit}</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                      className={`h-2 rounded-full bg-gradient-to-r ${
                        percentage > 80 ? 'from-red-500 to-orange-500' :
                        percentage > 60 ? 'from-yellow-500 to-orange-500' :
                        'from-green-500 to-emerald-500'
                      }`}
                    />
                  </div>
                </div>
                
                <p className="text-xs text-gray-400">
                  {percentage.toFixed(0)}% used this month
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Current Plan & Billing Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-white">Choose Your Plan</h2>
            <div className="flex items-center gap-4">
              <span className={`text-sm ${billingPeriod === 'monthly' ? 'text-white' : 'text-gray-400'}`}>
                Monthly
              </span>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  billingPeriod === 'yearly' ? 'bg-purple-500' : 'bg-gray-600'
                }`}
              >
                <motion.div
                  animate={{ x: billingPeriod === 'yearly' ? 24 : 0 }}
                  className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full"
                />
              </motion.button>
              <div className="flex items-center gap-2">
                <span className={`text-sm ${billingPeriod === 'yearly' ? 'text-white' : 'text-gray-400'}`}>
                  Yearly
                </span>
                <span className="px-2 py-1 text-xs bg-green-500 text-white rounded-lg">Save 17%</span>
              </div>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className={`relative p-6 rounded-2xl ${plan.popular ? 'ring-2 ring-purple-500' : ''}`}
                style={{
                  background: 'rgba(30, 41, 59, 0.8)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-3 py-1 text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                {plan.current && (
                  <div className="absolute top-4 right-4">
                    <span className="px-2 py-1 text-xs bg-green-500 text-white rounded-lg">Current</span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-3xl font-bold text-white">${plan.price}</span>
                    <span className="text-gray-400">/{plan.period}</span>
                  </div>
                  {billingPeriod === 'yearly' && (
                    <p className="text-sm text-green-400">
                      Save ${(plan.price / 10 * 12 - plan.price).toFixed(0)} per year
                    </p>
                  )}
                </div>

                <div className="space-y-3 mb-6">
                  {plan.features.map((feature, featureIndex) => (
                    <motion.div
                      key={featureIndex}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + index * 0.1 + featureIndex * 0.05 }}
                      className="flex items-center gap-3"
                    >
                      <FiCheck className="text-green-400 flex-shrink-0" size={16} />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={plan.current}
                  className={`w-full py-3 rounded-lg font-medium transition-all ${
                    plan.current
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : `bg-gradient-to-r ${plan.color} text-white hover:shadow-lg`
                  }`}
                  onClick={() => handleUpgrade(plan.name)}
                >
                  {plan.current ? 'Current Plan' : `Upgrade to ${plan.name}`}
                </motion.button>
                {!plan.current && (
                  <div className="mt-2 text-center">
                    <button onClick={handleManageBilling} className="text-xs text-gray-400 hover:text-gray-200 underline">Manage Billing</button>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Payment Methods */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="p-6 rounded-2xl"
            style={{
              background: 'rgba(30, 41, 59, 0.8)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <FiCreditCard className="text-blue-400" />
                Payment Methods
              </h2>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddPayment(true)}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium flex items-center gap-2"
              >
                <FiPlus size={16} />
                Add Card
              </motion.button>
            </div>

            <div className="space-y-3">
              {paymentMethods.map((method, index) => (
                <motion.div
                  key={method.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-gray-800 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <FiCreditCard className="text-blue-400" size={20} />
                    </div>
                    <div>
                      <p className="text-white font-medium">
                        {method.brand} •••• {method.last4}
                      </p>
                      <p className="text-gray-400 text-sm">
                        Expires {method.expiryMonth}/{method.expiryYear}
                        {method.isDefault && (
                          <span className="ml-2 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                            Default
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-2 text-gray-400 hover:text-white"
                    >
                      <FiEdit2 size={16} />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-2 text-gray-400 hover:text-red-400"
                    >
                      <FiTrash2 size={16} />
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Billing History */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="p-6 rounded-2xl"
            style={{
              background: 'rgba(30, 41, 59, 0.8)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <FiCalendar className="text-purple-400" />
              Billing History
            </h2>

            <div className="space-y-3">
              {invoices.length === 0 ? (
                <div className="text-center py-8">
                  <FiCalendar className="mx-auto text-gray-500 mb-4" size={48} />
                  <p className="text-gray-400 font-medium mb-1">No invoices yet</p>
                  <p className="text-gray-500 text-sm">Your billing history will appear here</p>
                </div>
              ) : (
                invoices.map((invoice, index) => (
                  <motion.div
                    key={invoice.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="flex items-center justify-between p-4 bg-gray-800 rounded-lg"
                  >
                    <div>
                      <p className="text-white font-medium">{invoice.description}</p>
                      <p className="text-gray-400 text-sm">
                        {invoice.date.toLocaleDateString()} • {invoice.id}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-white font-semibold">${invoice.amount}</p>
                        <div className={`flex items-center gap-1 text-sm ${getStatusColor(invoice.status)}`}>
                          {getStatusIcon(invoice.status)}
                          <span className="capitalize">{invoice.status}</span>
                        </div>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 text-gray-400 hover:text-white"
                        onClick={() => { if (invoice.downloadUrl) window.open(invoice.downloadUrl, '_blank'); else toast.error('No download available'); }}
                      >
                        <FiDownload size={16} />
                      </motion.button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full mt-4 p-3 bg-gray-800 text-gray-300 rounded-lg font-medium hover:bg-gray-700 flex items-center justify-center gap-2"
            >
              View All Invoices
              <FiArrowRight size={16} />
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Add Payment Modal */}
      <AddPaymentModal />
    </div>
  );
};

export default Billing;
